import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Accordion, BoxFull, Button, Dropdown, InputText, TabView, TransactionalSubheader } from "iuvity-ui-components";
import { CustomInput, Header } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { TenantDataTableActions } from "../../components/tenants/TenantDatatableActions";
import { useTenants } from "../../hooks/useTenants";
import { AppContext } from "../../context";
import { Controller, useForm, useWatch } from "react-hook-form";
import { TenantCreatorSchema } from "../../validations/schemas";
import { ErrorMessage } from "@hookform/error-message";
import { ColorPicker } from "primereact/colorpicker";
import { FileUpload } from "primereact/fileupload";
import { useTenantBranding } from "../../hooks/useTenantBranding";
import { REVALIDATE_FORM_PROPS, TENANT_STATES } from "../../constants";
import { Tenant } from "../../interfaces";
import { Tooltip } from 'primereact/tooltip';
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { Tag } from "iuvity-ui-components/dist/tag";
import { TenantRoleTable } from "../../components/tenants/TenantRoles";
import { formatStringToDate } from "../../helpers/formatDate";
import { useRoles } from "../../hooks/useRoles";
import { RolesAdminComponent } from "../../components/tenants/RolesAdminComponent";
import { TreeSelect } from 'primereact/treeselect';
import { DeleteTenant } from "../../components/tenants/DeleteTenant";


export const tenantCreatorDefaultValues = {
  id: "",
  guid: "",
  name: "",
  tenantcode: "",
  alias: "",
  createdat: "",
  parentid: "",
  status: "",
  primarycolor: "",
  secondarycolor: "",
  logo: "",
  isotypelogo: "",
  logoname: "",
  isotypelogoname: "",
};

export const tenantDeleteDefaultValues = {
  id: "",
  tenantcode: "",
  name: "",
  createdat: "",
  alias: "",
  updatedat: "",
  parentid: "",
  status: "",
  guid: "",

  primarycolor: "",
  secondarycolor: "",
  logo: "",
  isotypelogo: "",
  logoname: "",
  isotypelogoname: "",
};

export const ViewTenantConfigurationPage = () => {
  const navigate = useNavigate();
  const {
    t,
    appState: { user, selectedTenant, edit, permissiondInfoByRole, language },
    setAppState,
  } = useContext(AppContext);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [onEdit, setOnEdit] = useState<boolean>(edit ?? false);

  const selectedTenantFormatted = {
    ...selectedTenant,
    logo: selectedTenant?.images?.filter((item: any) => item.entity === "TENANT_BRANDING_LOGO")[0],
    isotypelogo: selectedTenant?.images?.filter((item: any) => item.entity === "TENANT_BRANDING_ISOTYPE_LOGO")[0],
    logoname: selectedTenant?.images?.filter((item: any) => item.entity === "TENANT_BRANDING_LOGO")[0]?.name,
    isotypelogoname: selectedTenant?.images?.filter((item: any) => item.entity === "TENANT_BRANDING_ISOTYPE_LOGO")[0]?.name
  }

  const {
    control,
    getValues,
    setValue,
    reset,
    formState: { errors, isDirty, isValid },
  } = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: {
      id: selectedTenant?.id,
      tenantcode: selectedTenant?.tenantcode,
      name: selectedTenant?.name,
      guid: selectedTenant?.guid,
      parentid: selectedTenant?.parentid,
      shortname: selectedTenant?.shortname,
      alias: selectedTenant?.alias,
      status: selectedTenant?.status,
      createdat: formatStringToDate(selectedTenant?.createdat, true, t),
      updatedat: selectedTenant?.updatedat ? formatStringToDate(selectedTenant?.updatedat, true, t) : '',
      primarycolor: selectedTenant?.tenantbranding?.primarycolor?.slice(1, 7),
      secondarycolor: selectedTenant?.tenantbranding?.secondarycolor?.slice(1, 7),
      logo: selectedTenantFormatted?.logo || '',
      isotypelogo: selectedTenantFormatted?.isotypelogo || '',
      logoname: selectedTenantFormatted?.logoname || '',
      isotypelogoname: selectedTenantFormatted?.isotypelogoname || '',
    },
  });

  const { tenantAction } = useTenants("ACTIONS");
  const { getBrandingInfo } = useTenantBranding("DATA");
  const { data, rolesAction, reloadFn } = useRoles("DATA", undefined, selectedTenant?.id);
  const { data: tenants } = useTenants("DATA");
  const [dataBranding, setDataBranding] = useState<any>();
  const [treeTenants, setTreeTenants] = useState<any>([]);
  const isDeleted = selectedTenant?.status == 'DELETED';


  useEffect(() => {
    (async () => {
      const data = await Promise.resolve(getBrandingInfo(selectedTenant?.id!));
      setDataBranding(data);

      const buttonCopy = document.querySelector("i.iu-icon-copy") as HTMLElement;
      if (buttonCopy) {
        buttonCopy.setAttribute('title', t('Tenants.textCopyButton'))
        buttonCopy.addEventListener("click", function () {
          const guid = selectedTenant?.guid || '';
          navigator.clipboard.writeText(guid);
        });
      }

    })();
  }, []);


  useEffect(() => {
    (async () => {
      const buttonCopy = document.querySelector("i.iu-icon-copy") as HTMLElement;
      if (buttonCopy) {
        buttonCopy.setAttribute('title', t('Tenants.textCopyButton'))
      }
    })();
  }, [language]);

  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const [layerName, setLayerName] = useState<{
    logo: string,
    isotypelogo: string,
  }>({
    logo: selectedTenantFormatted?.logoname || '',
    isotypelogo: selectedTenantFormatted?.isotypelogoname || ''
  })

  const [layerNameValidator, setLayerNameValidator] = useState<{
    logo: string,
    isotypelogo: string,
  }>({
    logo: selectedTenantFormatted?.logoname || '',
    isotypelogo: selectedTenantFormatted?.isotypelogoname || ''
  })

  const [hasImageError, setHasImageError] = useState<{
    logo: boolean,
    isotypelogo: boolean,
  }>({
    logo: true,
    isotypelogo: true
  })

  const [disableUploadFile, setDisableUploadFile] = useState<{
    logo: boolean;
    isotypelogo: boolean;
  }>({
    logo: false,
    isotypelogo: false,
  })

  const invoiceUploadHandler = ({ files }: any, key: 'logo' | 'isotypelogo') => {
    setDisableUploadFile((prev: any) => ({
      ...prev,
      [key]: true,
    }));
    setValue(key, files[0], REVALIDATE_FORM_PROPS);
    const splitLayerName = files[0].name.split('.');
    const limit = 20;
    let fileName = files[0].name;

    // get both parts
    let fileNameExtension = "." + fileName.split(".").pop();
    const limitName = limit - fileNameExtension.length
    let fileNameBase = fileName.substring(0, limit - fileNameExtension.length);

    fileName = fileNameBase + fileNameExtension;

    const formatLayerName = `${fileName}`;
    console.log(splitLayerName[0].slice(0, limitName), fileNameBase);
    setLayerNameValidator((prev) => ({
      ...prev,
      [key]: splitLayerName[0].slice(0, limitName)
    }));
    key === 'logo'
      ? setValue('logoname', splitLayerName[0].slice(0, limitName))
      : setValue('isotypelogoname', splitLayerName[0].slice(0, limitName));
    setLayerName((prev) => ({
      ...prev,
      [key]: formatLayerName
    }));
  }

  const invoiceRemoveHandler = (key: 'logo' | 'isotypelogo') => {
    setDisableUploadFile((prev: any) => ({
      ...prev,
      [key]: false,
    }));
    // @ts-ignore
    setValue(key, selectedTenantFormatted?.[key], REVALIDATE_FORM_PROPS);
    key === 'logo'
      ? setValue('logoname', selectedTenantFormatted?.logoname!, REVALIDATE_FORM_PROPS)
      : setValue('isotypelogoname', selectedTenantFormatted?.isotypelogoname!, REVALIDATE_FORM_PROPS);
    setLayerName((prev) => ({
      ...prev,
      [key]: key === 'logo' ? selectedTenantFormatted?.logoname : selectedTenantFormatted?.isotypelogoname
    }));
    setLayerNameValidator((prev) => ({
      ...prev,
      [key]: key === 'logo' ? selectedTenantFormatted?.logoname : selectedTenantFormatted?.isotypelogoname
    }));
  }
  const [visibleModalDelete, setVisibleModalDelete] = useState<boolean>(false);

  const onDeleteAction = async (tenant: Tenant) => {
    await tenantAction("DELETE", tenant.id);
    reloadFn();
  };
  const handleModalPropsDelete = async (
    type: string,
    tenant?: Tenant | null
  ) => {
    switch (type) {
      case "DELETE":
        await onDeleteAction(tenant!);
        setAppState((prev) => ({
          ...prev,
          selectedTenant: null,
        }));
        setVisibleModalDelete(false);
        navigate('/tenants');
        return;
      default:
        return;
    }
  };
  const logoValues = useWatch({
    control: control,
    name: "logo",
  });

  const isotypelogoValues = useWatch({
    control: control,
    name: "isotypelogo",
  });

  const tenantcodeValues = useWatch({
    control: control,
    name: "tenantcode",
  });

  const onSave = async () => {
    const { createdat, ...formValues } = getValues();

    const actionStatus = await tenantAction("UPDATE",
      {
        ...formValues,
        alias: formValues?.alias?.toUpperCase(),
        logo: getValues('logo')['id'] ? '' : getValues('logo'),
        isotypelogo: getValues('isotypelogo')['id'] ? '' : getValues('isotypelogo'),
        primarycolor: `#${formValues.primarycolor}`,
        secondarycolor: `#${formValues.secondarycolor}`,
        logoName: formValues.logoname,
        isotypelogoName: formValues.isotypelogoname,
        updatedby: user?.name,
        guid: selectedTenant?.guid
      },
    );

    if (actionStatus === "SUCCESS") {
      reset(formValues);
      setAppState((prev) => ({
        ...prev,
        selectedTenant: formValues as Tenant,
      }));
    }
  };

  const onCancel = () => {
    navigate("/tenants");
  };

  const handleModalProps = (isCancelButton?: boolean) => {
    setModalProps((prev) => ({
      ...prev,
      visible: true,
      title: isCancelButton
        ? t("Tenants.cancelModalTitle")
        : t("Tenants.saveModalTitle"),
      content: isCancelButton
        ? t("Tenants.cancelModalContent")
        : t("Tenants.saveModalContent"),
      onConfirm: isCancelButton ? onCancel : onSave,
    }));
  };

  const goToEdit = (tenant?: any) => {
    setOnEdit(true);
  };

  useEffect(() => {
    setHasImageError({
      logo: false,
      isotypelogo: false
    })
    if (layerNameValidator.logo !== '' && !(/^([a-zA-Z0-9\\sÁÉÍÓÚáéíóúÑñ_]+)$/.test(layerNameValidator.logo))) {
      setHasImageError((prev) => ({
        ...prev,
        logo: true,
      }))
    }
    if (layerNameValidator.isotypelogo !== '' && !(/^([a-zA-Z0-9\\sÁÉÍÓÚáéíóúÑñ_]+)$/.test(layerNameValidator.isotypelogo))) {
      setHasImageError((prev) => ({
        ...prev,
        isotypelogo: true,
      }))
    }
    layerNameValidator.logo === '' &&
      setHasImageError((prev) => ({
        ...prev,
        logo: true,
      }));
    layerNameValidator.isotypelogo === '' &&
      setHasImageError((prev) => ({
        ...prev,
        isotypelogo: true,
      }));
  }, [layerNameValidator])


  useEffect(() => {
    if (!tenants) return;

    if (tenants.length == 1) {
      const tenant = tenants[0];
      if (tenant.id == selectedTenant?.id && selectedTenant?.parentid) {
        const parent = tenant.parent;
        setTreeTenants([{
          id: parent.id,
          key: parent.id,
          label: parent.name,
          data: parent.id,
          parentid: parent.parentid ?? null,
          children: []
        }
        ]);
      } else {
        setTreeTenants([{
          id: tenant.id,
          key: tenant.id,
          label: tenant.name,
          data: tenant.id,
          parentid: tenant.parentid ?? null,
          children: []
        }
        ]);
      }
      return;
    }

    let tenantsData: any = tenants?.map((tenant: any) => {
      return {
        id: tenant.id,
        key: tenant.id,
        label: tenant.name,
        data: tenant.id,
        parentid: tenant.parentid ?? null,
        parent: tenant.parent ?? null,
        children: []
      };
    });


    // Map element ID to arr index
    const idMapping = tenantsData.reduce((acc: any, el: any, i: any) => {
      acc[el.id] = i;
      return acc;
    }, {});

    let root: any;
    // Push each element to parent's children array
    let hasOneRoot = false;
    tenantsData.forEach((el: any) => {
      // Handle the root element
      if (!hasOneRoot && (el.parentid == null || (el.parentid != null && tenantsData.filter((ten: any) => ten.parentid == el.id).length > 0))) {

        root = el;
        hasOneRoot = true;
        return;
      }
      // Use our mapping to locate the parent element in our data array
      const parentEl = tenantsData[idMapping[el.parentid]];
      if (!parentEl) return;
      // Add our current el to its parent's `children` array
      parentEl.children = [...(parentEl.children || []), el];
    });
    if (root && root.id == selectedTenant?.id && selectedTenant?.parentid) {
      root = {
        id: root.parent.id,
        key: root.parent.id,
        label: root.parent.name,
        data: root.parent.id,
        children: [root]
      }

    }

    setTreeTenants([root])
  }, [tenants])
  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}

      <TransactionalSubheader
        subheaderTitle={
          <div style={{ position: "relative", left: "-40px" }}>
            <i
              className="iu iu-icon-left pr-3"
              onClick={() => {
                setAppState((prev) => ({
                  ...prev,
                  functionalityCode: '/tenants',
                }));
                navigate("/tenants");
              }}
              aria-hidden="true"
              style={{
                fontSize: "var(--font-size-tablet-title)",
                cursor: "pointer",
              }}
            />
            {!onEdit && t("Tenants.detailTenantTitle")}
            {onEdit && t("Tenants.editTenantTitle")}

            <Tag value={selectedTenant?.name} className='tag-tenant-name' />

          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t("Tenants.detailTenantTitle")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("Tenants.advancedOptionsBreadcrumbLabel"),
              style: { fontWeight: "bold" },
              disabled: true
            },
            {
              label: t("Tenants.detailTenantTitle"),
              style: { fontFamily: "var(--fontBarlowSemiBold)" },
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />

      </TransactionalSubheader>

      <BoxFull className="my-4 px-0 py-0" variant="tertiary">
        <TabView
          className="upload-tabview mt-4"
          activeIndex={activeIndex}
          onTabChange={(e) => {
            setActiveIndex(e.index);
            setAppState((prev) => ({
              ...prev,
              functionalityCode: e.index === 1
                ? '/applications'
                : (e.index === 2
                  ? '/adminroles'
                  : '/tenants'),
            }));
          }}
          items={[
            {
              key: "1",
              header: t("Tenants.viewInformation"),
              children: (
                <>
                  <BoxFull className="flex align-items-center justify-content-center pt-5 pb-0" variant="tertiary">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      {permissiondInfoByRole.UPDATE && !onEdit && !isDeleted && <SubheaderActions
                        className="px-0 pt-4 pb-3"
                        actions={[
                          {
                            id: "111",
                            icon: "iu iu-icon-edit",
                            label: t("Tenants.editTenantBtnLabel"),
                            onClick: () => { goToEdit(selectedTenant) }
                          },
                        ]}
                      />
                      }
                      <p style={{ marginBottom: 'var(--space-base)' }}>
                        {onEdit && t("Tenants.editTenantSubtitle")}
                        {(!onEdit || isDeleted) && t("Tenants.detailTenantSubtitle")}

                      </p>
                      <div className="form-container md py-5 w-full">
                        <div className="form md w-full">
                          <div className="flex align-items-flex-start justify-content-flex-start flex-wrap">
                            {Object.keys(isDeleted ? tenantDeleteDefaultValues : tenantCreatorDefaultValues).map((key, idx, arr) => (
                              <div
                                key={`c-${key}`}
                                className={`flex flex-column mb-0 px-6`}
                                style={{ width: "50%" }}
                              >
                                <Controller
                                  key={key}
                                  name={key as any}
                                  control={control}
                                  rules={(TenantCreatorSchema(t) as any)[key]}
                                  render={({ field }) => {
                                    if (["id", "name", "alias", "tenantcode"].includes(key)) {
                                      return <div className="input-tenant mb-3" style={{ paddingLeft: "3rem" }}>
                                        <InputText
                                          id={`input-tenant-${key}`}
                                          label={t(`Tenants.${key}Label`)}
                                          placeholder={onEdit ? t(`Tenants.${key}Placeholder`) : ''}
                                          type="text"
                                          autoComplete="off"
                                          style={{
                                            width: '20rem !important',
                                            textTransform: (
                                              !!getValues('alias') && key === 'alias'
                                            ) ? 'uppercase' : undefined,
                                          }}
                                          keyfilter={
                                            (TenantCreatorSchema(t) as any)[key]?.keyfilter
                                          }
                                          maxLength={
                                            (TenantCreatorSchema(t) as any)[key]?.maxLength?.value
                                          }
                                          {...field}
                                          hasTooltipRef={key=='tenantcode'}
                                          tooltipIdRef={key}
                                          tooltipRef={{ id: key, content: t(`Tenants.${key}Tooltip`) }}
                                          isReadOnly={["id"].includes(key) || !onEdit}
                                        />
                                        {['name','alias', 'tenantcode'].includes(key) &&
                                          <ErrorMessage
                                            errors={errors}
                                            name={key as any}
                                            //@ts-ignore
                                            render={({ message }) => (
                                              <small
                                                key={`small-${key}`}
                                                className="p-error block"
                                                style={{
                                                  marginTop: "0.5rem",
                                                  marginBottom: "1.5rem",
                                                  marginLeft: "3rem",
                                                }}
                                              >
                                                {message}
                                              </small>
                                            )}
                                          />}
                                      </div>
                                    }
                                    if (key === "guid") {
                                      return <div className="input-tenant mb-3" style={{ paddingLeft: "3rem" }}>
                                        <InputText
                                          id={`input-tenant-${key}`}
                                          label={t(`Tenants.${key}Label`)}
                                          placeholder={t(`Tenants.${key}Placeholder`)}
                                          type="text"
                                          autoComplete="off"
                                          style={{
                                            width: '20rem !important',
                                          }}
                                          {...field}
                                          isReadOnly={true}
                                          hasTooltipRef={true}
                                          tooltipIdRef={key}
                                          tooltipRef={{ id: key, content: t(`Tenants.${key}Tooltip`) }}
                                          hasIcon
                                          iconPosition="right"
                                          iconName="iu iu-icon-copy"
                                        />
                                      </div>
                                    }
                                    if (key === "createdat") {
                                      return <div className="input-tenant mb-3" style={{ paddingLeft: "3rem" }}>
                                        <InputText
                                          id={`input-tenant-${key}`}
                                          label={t(`Tenants.${key}Label`)}
                                          placeholder={t(`Tenants.${key}Placeholder`)}
                                          type="text"
                                          autoComplete="off"
                                          style={{
                                            width: '20rem !important',
                                          }}
                                          {...field}
                                          isReadOnly={true}
                                          hasIcon
                                          iconPosition="left"
                                          iconName="iu iu-icon-calendar"
                                        />
                                      </div>
                                    }
                                    if (isDeleted && key == 'updatedat') {
                                      return <div className="input-tenant mb-3" style={{ paddingLeft: "3rem" }}>
                                        <InputText
                                          id={`input-tenant-${key}`}
                                          label={t(`Tenants.${key}Label`)}
                                          placeholder={t(`Tenants.${key}Placeholder`)}
                                          type="text"
                                          autoComplete="off"
                                          style={{
                                            width: '20rem !important',
                                          }}
                                          {...field}
                                          isReadOnly={true}
                                          hasIcon
                                          iconPosition="left"
                                          iconName="iu iu-icon-calendar"
                                        />
                                      </div>
                                    }
                                    if (["parentid"].includes(key) && field.value != null && onEdit) {
                                      return <div style={{ paddingLeft: "3rem" }}>
                                        <label
                                          htmlFor={`input-tenant-${key}`}
                                          className={`mb-2 block lb}`}
                                        >{t(`Tenants.parentLabel`)}
                                          <span className={`tooltip-inputtext iu iu-icon-info tooltip-id-${key}`}
                                            style={{ color: "var(--successColor)", marginLeft: "1rem", }} >
                                            <Tooltip target={`.tooltip-id-${key}`} position="bottom">
                                              <p>{t(`Tenants.${key}Tooltip`)}</p>
                                            </Tooltip>
                                          </span>
                                        </label>
                                        <TreeSelect filter selectionMode="single" id={`input-tenant-${key}`} options={treeTenants} panelClassName="select-tenant" className="md:w-20rem w-full" placeholder={t(`Tenants.parentPlaceholder`)} {...field}></TreeSelect>
                                      </div>
                                    }
                                    if (["parentid"].includes(key) && field.value != null && !onEdit) {
                                      return <div className="input-tenant mb-3" style={{ paddingLeft: "3rem" }}>
                                        <InputText
                                          id={`input-tenant-parent-read-only`}
                                          label={t(`Tenants.parentLabel`)}
                                          placeholder={t(`Tenants.parentPlaceholder`)}
                                          type="text"
                                          autoComplete="off"
                                          className="ibp-readonly "
                                          style={{
                                            width: '20rem !important',
                                          }}
                                          value={selectedTenant?.parent?.name}
                                          isReadOnly={true}
                                          hasTooltipRef={true}
                                          tooltipIdRef={key}
                                          tooltipRef={{ id: key, content: t(`Tenants.${key}Tooltip`) }}
                                        />
                                      </div>
                                    }

                                    if (["parentid"].includes(key) && field.value == null) {
                                      return <div className="input-tenant mb-3" style={{ paddingLeft: "3rem" }}>
                                        <InputText
                                          id={`input-tenant-parent-read-only`}
                                          label={t(`Tenants.parentLabel`)}
                                          placeholder={t(`Tenants.parentPlaceholder`)}
                                          type="text"
                                          autoComplete="off"
                                          className="ibp-readonly "
                                          style={{
                                            width: '20rem !important',
                                          }}
                                          value={selectedTenant?.name}
                                          isReadOnly={true}
                                          hasTooltipRef={true}
                                          tooltipIdRef={key}
                                          tooltipRef={{ id: key, content: t(`Tenants.${key}Tooltip`) }}
                                        />
                                      </div>
                                    }
                                    if (["status"].includes(key)) {
                                      return <div style={{ paddingLeft: "3rem" }}>
                                        <Dropdown
                                          key={key}
                                          id={`dropdown-tenant-${key}`}
                                          className="dropdown dropdown-tenant"
                                          label={t(`Tenants.statusLabel`)}
                                          placeholder={t(`Tenants.statusPlaceholder`)}
                                          options={TENANT_STATES(t, field.value == 'DELETED')}
                                          filter={false}
                                          showClear={false}
                                          readOnly={!onEdit}
                                          width="20rem"
                                          {...field}
                                        />
                                      </div>
                                    }
                                    return <></>

                                  }}
                                />
                                {
                                  ['shortname'].includes(key) &&
                                  <ErrorMessage
                                    errors={errors}
                                    name={key as any}
                                    //@ts-ignore
                                    render={({ message }) => (
                                      <small
                                        key={`small-${key}`}
                                        className="p-error block"
                                        style={{
                                          marginTop: "0.5rem",
                                          marginBottom: "1.5rem",
                                          marginLeft: "3rem",
                                        }}
                                      >
                                        {message}
                                      </small>
                                    )}
                                  />
                                }
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="colors_title">
                        <span>{t("Tenants.customerColorsTitle")}</span>
                      </div>
                      <div className="form-container md py-5 w-full">
                        <div className="form md w-full">
                          <div className="flex align-items-flex-start justify-content-flex-start flex-wrap">
                            {Object.keys(tenantCreatorDefaultValues).filter((key) => key !== 'id' && key !== 'status').map((key, idx, arr) => (
                              <div
                                key={`c-${key}`}
                                className={`flex flex-column mb-0 px-6`}
                                style={{ width: "50%" }}
                              >
                                <Controller
                                  key={key}
                                  name={key as any}
                                  control={control}
                                  rules={(TenantCreatorSchema(t) as any)[key]}
                                  render={({ field }) => {
                                    return (key === "primarycolor" || key === "secondarycolor") ? (
                                      <div style={{ paddingLeft: "3rem" }}>
                                        <>
                                          {['primarycolor', 'secondarycolor'].includes(key) &&
                                            <div className="mb-2" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                                              <label htmlFor={`input-${key}`} className="block lb ">
                                                {t(`Tenants.${key}Label`)}
                                              </label>
                                              <div className="tenant-tooltip">
                                                <Tooltip target=".custom-target-icon" />
                                                <i
                                                  className="custom-target-icon iu iu-icon-info"
                                                  data-pr-tooltip={t(`Tenants.${key}Tooltip`)}
                                                  data-pr-position="right"
                                                  style={{ color: "var(--successColor)" }}
                                                />
                                              </div>
                                            </div>
                                          }
                                          <div className={`${['primarycolor', 'secondarycolor'].includes(key) ? 'flex' : ''}`}>
                                            {['primarycolor', 'secondarycolor'].includes(key) &&
                                              (
                                                <>
                                                  <ColorPicker
                                                    id="tenant-colorPicker"
                                                    format="hex"
                                                    value={getValues(key)}
                                                    defaultColor="#cccccc"
                                                    readOnly={true}
                                                    style={{
                                                      marginRight: "var(--marginLabelInput)",
                                                    }}
                                                    onClick={() => { }}
                                                  />
                                                  <p>#</p>
                                                </>
                                              )
                                            }
                                            <CustomInput
                                              id={`input-${key}`}
                                              className={
                                                // @ts-ignore
                                                errors[key]?.message
                                                  ? "p-invalid"
                                                  : idx !== arr.length - 1
                                                    ? "mb-3"
                                                    : ""
                                              }
                                              label={['primarycolor', 'secondarycolor'].includes(key) ? undefined : t(`Tenants.${key}Label`)}
                                              placeholder={t(`Tenants.${key}Placeholder`)}
                                              type="text"
                                              isReadOnly={!onEdit}

                                              autoComplete="off"
                                              style={{
                                                width: '10rem !important',
                                              }}
                                              maxLength={
                                                (TenantCreatorSchema(t) as any)[key]?.maxLength?.value
                                              }
                                              {...field}
                                            />
                                          </div>
                                        </>
                                      </div>
                                    ) : <></>
                                  }}
                                />
                                {
                                  ['primarycolor', 'secondarycolor'].includes(key) &&
                                  <ErrorMessage
                                    errors={errors}
                                    name={key as any}
                                    //@ts-ignore
                                    render={({ message }) => (
                                      <small
                                        key={`small-${key}`}
                                        className="p-error block"
                                        style={{
                                          marginTop: "0.5rem",
                                          marginLeft: "3rem",
                                        }}
                                      >
                                        {message}
                                      </small>
                                    )}
                                  />
                                }
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="logotype_title">
                        <span>{t("Tenants.customerLogoTitle")}</span>
                        <Tooltip target=".custom-target-icon" />
                        <i
                          className="custom-target-icon iu iu-icon-info"
                          data-pr-tooltip={t(`Tenants.uploadFileLogoTooltip`)}
                          data-pr-position="right"
                          style={{ color: "var(--successColor)", marginLeft: "0.5rem" }}
                        />
                      </div>
                      <p style={{ margin: '0 2rem var(--space-base) 2rem' }}>{
                        t("Tenants.customerLogoSubtitle")}
                      </p>
                      <div>
                        {Object.keys(tenantCreatorDefaultValues).map((key, idx, arr) => (
                          <div
                            key={`c-${key}`}
                          >
                            <Controller
                              key={key}
                              name={key as any}

                              control={control}
                              rules={(TenantCreatorSchema(t) as any)[key]}
                              render={({ field }) => {
                                return (key === "logo") ? (
                                  <div className="card">
                                    <FileUpload
                                      disabled={!onEdit}
                                      accept="image/*"
                                      // maxFileSize={1000000}
                                      emptyTemplate={
                                        <div
                                          className="flex align-items-center pl-3"
                                          style={{
                                            height: '100%',
                                            justifyContent: 'space-around',
                                          }}
                                        >
                                          <div
                                            style={{
                                              padding: '1rem',
                                            }}
                                          >
                                            <img
                                              // src="https://dev.w3.org/SVG/tools/svgweb/samples/svg-files/410.svg"
                                              src={dataBranding?.logo?.url}
                                              alt="logo"
                                              style={{
                                                height: '50px',
                                                width: 'var(--padding-level-eighty)',
                                              }}
                                            />
                                          </div>
                                          <div>
                                            {`${selectedTenantFormatted?.logoname}.${selectedTenantFormatted?.logo?.metadata?.extensionFile}`}
                                          </div>
                                          <div>
                                            {`${(selectedTenantFormatted?.logo?.metadata?.size / 1028).toFixed(2)} KB`}
                                          </div>
                                        </div>
                                      }
                                      cancelOptions={{
                                        style: {
                                          display: 'none',
                                        }
                                      }}
                                      uploadOptions={{
                                        style: {
                                          display: 'none',
                                        }
                                      }}
                                      chooseLabel={
                                        disableUploadFile.logo ? t("PointsOfAttention.fileLabel") : t("Tenants.replaceFileBtnLabel")
                                      }
                                      chooseOptions={{
                                        icon: 'iu iu-icon-upload',
                                        className: disableUploadFile.logo ? ' p-disabled p-button-link' : 'p-button-link',
                                      }}
                                      onSelect={(e) => invoiceUploadHandler(e, 'logo')}
                                      onRemove={() => invoiceRemoveHandler('logo')}
                                    />
                                  </div>
                                ) : <></>
                              }}
                            />
                            {['logo'].includes(key) &&
                              hasImageError.logo
                              ?
                              <small
                                key={`small-logo`}
                                className="p-error block"
                                style={{
                                  marginTop: "0.5rem",
                                  marginBottom: "1.5rem",
                                }}
                              >
                                {
                                  layerNameValidator.logo === ''
                                    ?
                                    !selectedTenantFormatted?.logo
                                      ? (<ErrorMessage
                                        errors={errors}
                                        name="logo"
                                        //@ts-ignore
                                        render={({ message }) => (
                                          <small
                                            className="p-error block"
                                            style={{
                                              marginTop: "0.5rem",
                                            }}
                                          >
                                            {message}
                                          </small>
                                        )}
                                      />)
                                      : null
                                    : t("GlobalConfigurations.generalErrorMessage")
                                }
                              </small>
                              : null
                            }
                          </div>
                        ))}
                      </div>

                      <div className="logotype_title">
                        <span>{t("Tenants.customerIsotypeTitle")}</span>
                        <Tooltip target=".custom-target-icon" />
                        <i
                          className="custom-target-icon iu iu-icon-info"
                          data-pr-tooltip={t(`Tenants.uploadFileIsotypeTooltip`)}
                          data-pr-position="right"
                          style={{ color: "var(--successColor)", marginLeft: "0.5rem" }}
                        />
                      </div>
                      <div>
                        {Object.keys(tenantCreatorDefaultValues).map((key, idx, arr) => (
                          <div
                            key={`c-${key}`}
                          >
                            <Controller
                              key={key}
                              name={key as any}
                              control={control}
                              rules={(TenantCreatorSchema(t) as any)[key]}
                              render={({ field }) => {
                                return (key === "isotypelogo") ? (
                                  <div className="card">
                                    <FileUpload
                                      disabled={!onEdit}
                                      accept="image/*"
                                      // maxFileSize={1000000}
                                      emptyTemplate={
                                        <div
                                          className="flex align-items-center pl-3"
                                          style={{
                                            height: '100%',
                                            justifyContent: 'space-around',
                                          }}
                                        >
                                          <div
                                            style={{
                                              padding: '1rem',
                                            }}
                                          >
                                            <img
                                              src={dataBranding?.isotypelogo?.url}
                                              alt="isotype"
                                              style={{
                                                height: '50px',
                                                width: 'var(--padding-level-eighty)',
                                              }}
                                            />
                                          </div>
                                          <div>
                                            {`${selectedTenantFormatted?.isotypelogoname}.${selectedTenantFormatted?.isotypelogo?.metadata?.extensionFile}`}
                                          </div>
                                          <div>
                                            {`${(selectedTenantFormatted?.isotypelogo?.metadata?.size / 1028).toFixed(2)} KB`}
                                          </div>
                                        </div>
                                      }
                                      cancelOptions={{
                                        style: {
                                          display: 'none',
                                        }
                                      }}
                                      uploadOptions={{
                                        style: {
                                          display: 'none',
                                        }
                                      }}
                                      chooseLabel={
                                        disableUploadFile.isotypelogo ? t("PointsOfAttention.fileLabel") : t("Tenants.replaceFileBtnLabel")
                                      }
                                      chooseOptions={{
                                        icon: 'iu iu-icon-upload',
                                        className: disableUploadFile.isotypelogo ? ' p-disabled p-button-link' : 'p-button-link',
                                      }}
                                      onSelect={(e) => invoiceUploadHandler(e, 'isotypelogo')}
                                      onRemove={() => invoiceRemoveHandler('isotypelogo')}
                                    />
                                  </div>
                                ) : <></>
                              }}
                            />
                            {['isotypelogo'].includes(key) &&
                              hasImageError.isotypelogo
                              ?
                              <small
                                key={`small-isotypelogo`}
                                className="p-error block"
                                style={{
                                  marginTop: "0.5rem",
                                  marginBottom: "1.5rem",
                                }}
                              >
                                {
                                  layerNameValidator.isotypelogo === ''
                                    ?
                                    !selectedTenantFormatted?.isotypelogo
                                      ? (<ErrorMessage
                                        errors={errors}
                                        name="isotypelogo"
                                        //@ts-ignore
                                        render={({ message }) => (
                                          <small
                                            className="p-error block"
                                            style={{
                                              marginTop: "0.5rem",
                                            }}
                                          >
                                            {message}
                                          </small>
                                        )}
                                      />)
                                      : null
                                    : t("GlobalConfigurations.generalErrorMessage")
                                }
                              </small>
                              : null
                            }
                          </div>
                        ))}
                      </div>

                    </div>
                  </BoxFull>

                  <BoxFull className="pt-5 px-4 pb-5" style={{ backgroundColor: "transparent" }}>
                    <div className="flex align-items-center justify-content-end gap-5">
                      {(!isDeleted && onEdit) && <Button
                        variant="p-button-link"
                        label={t("Tenants.cancelBtnLabel")}
                        onClick={!isDirty ? onCancel : () => handleModalProps(true)}
                      />}
                      {(permissiondInfoByRole.REMOVE && !isDeleted && !onEdit) && <Button
                        variant="p-button-link"
                        label={t("Tenants.deleteBtnLabel")}
                        onClick={() => setVisibleModalDelete(true)}
                      />
                      }

                      {(permissiondInfoByRole.UPDATE && !isDeleted && !onEdit) && <Button
                        variant="p-button"
                        label={t("Tenants.editBtnLabel")}
                        onClick={() => setOnEdit(true)}
                      />}
                      {(!isDeleted && onEdit) && <Button
                        variant="p-button"
                        label={t("Tenants.saveBtnLabel")}
                        disabled={!(
                          (isDirty &&
                            isValid &&
                            (logoValues && (layerName.logo !== selectedTenantFormatted?.logoname || isDirty)) &&
                            (isotypelogoValues && (layerName.isotypelogo !== selectedTenantFormatted?.isotypelogoname || isDirty)) &&
                            (tenantcodeValues && (getValues('tenantcode') !== selectedTenant?.tenantcode || isDirty)) &&
                            (((getValues('parentid') !== selectedTenant?.parentid) || isDirty)) &&
                            !hasImageError.logo &&
                            !hasImageError.isotypelogo)
                        )}
                        onClick={() => handleModalProps()}
                      />}
                    </div>
                  </BoxFull>

                </>
              ),
              disabled: false,
            },
            {
              key: "2",
              header: t("Tenants.viewApplications"),
              children: (

                <BoxFull className="pt-0" variant="tertiary">
                  <TenantDataTableActions auxModalController={setModalProps} />
                </BoxFull>

              ),
            },
            {
              key: "3",
              header: t("Tenants.viewRoles"),
              children: (
                <BoxFull className="pt-0" variant="tertiary">
                  <RolesAdminComponent
                    data={data}
                    rolesAction={rolesAction}
                    reloadFn={reloadFn}
                  />
                </BoxFull>
              )
            }
          ]}
        />
      </BoxFull>


      <DeleteTenant
        visible={visibleModalDelete}
        closeHandler={() => { setVisibleModalDelete(false) }}
        confirmHandler={() => handleModalPropsDelete("DELETE", selectedTenant)}
      />

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
